import { PUBLIC_PLANS } from '@/lib/billing/plans';
import type { Tier, FeatureGroup, FAQItem } from './types';

export const TIERS: Tier[] = PUBLIC_PLANS.map((plan) => ({
  id: plan.code,
  name: plan.name,
  sub: plan.audience,
  priceMonthly: plan.monthlyPriceInr,
  priceYearly: plan.yearlyPriceInr,
  caseLimit: plan.caseLimit,
  seatMinimum: plan.seatMinimum,
  isPopular: plan.isBestValue,
  requestAccessOnly: plan.isRequestAccessOnly,
  isYearlyOnly: plan.isYearlyOnly,
  monthlyEquivalentInr: plan.monthlyEquivalentInr,
  cardFeatures: plan.features,
  ctaLabel: plan.isRequestAccessOnly
    ? 'Request Access'
    : plan.code === 'firm'
      ? 'Start Firm Plan'
      : `Start ${plan.name}`,
  ctaHref: plan.isRequestAccessOnly ? '/request-access' : `/login?redirect=/pricing&tier=${plan.code}`,
  accent: 'brand',
}));

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    title: 'Case tracking',
    rows: [
      {
        label: 'Active monitored cases',
        values: {
          starter: '5',
          lawyer_pro: '500',
          firm: '500 shared/workspace',
          corporate: 'Custom',
        },
        highlight: true,
      },
      {
        label: 'Single-user plan',
        values: { starter: true, lawyer_pro: true, firm: false, corporate: false },
      },
      {
        label: 'Workspace dashboard',
        values: { starter: false, lawyer_pro: false, firm: true, corporate: true },
      },
      {
        label: 'Bulk CSV import',
        values: { starter: 'Up to 5 cases', lawyer_pro: true, firm: true, corporate: true },
      },
    ],
  },
  {
    title: 'Notifications',
    rows: [
      {
        label: 'WhatsApp/email alerts',
        values: { starter: true, lawyer_pro: true, firm: true, corporate: true },
      },
      {
        label: 'Hearing reminders',
        values: { starter: true, lawyer_pro: true, firm: true, corporate: true },
      },
      {
        label: 'Status/order updates',
        values: { starter: true, lawyer_pro: true, firm: true, corporate: true },
      },
      {
        label: 'Daily cause list',
        values: { starter: true, lawyer_pro: true, firm: true, corporate: true },
      },
      {
        label: 'Priority alerts',
        values: { starter: false, lawyer_pro: true, firm: true, corporate: true },
      },
    ],
  },
  {
    title: 'Workflow',
    rows: [
      {
        label: 'Calendar export',
        values: { starter: false, lawyer_pro: true, firm: true, corporate: true },
      },
      {
        label: 'Order archive',
        values: { starter: true, lawyer_pro: true, firm: true, corporate: 'Custom' },
      },
      {
        label: 'Advanced filters',
        values: { starter: false, lawyer_pro: true, firm: true, corporate: 'Custom' },
      },
      {
        label: 'API/custom workflow',
        values: { starter: false, lawyer_pro: false, firm: false, corporate: 'Custom' },
      },
    ],
  },
  {
    title: 'Team',
    rows: [
      {
        label: 'Seats',
        values: { starter: '1', lawyer_pro: '1', firm: '2 minimum', corporate: 'Custom' },
      },
      {
        label: 'Same organization/workspace',
        values: { starter: false, lawyer_pro: false, firm: true, corporate: true },
      },
      {
        label: 'Role-based access',
        values: { starter: false, lawyer_pro: false, firm: true, corporate: true },
      },
      {
        label: 'Consolidated billing',
        values: { starter: false, lawyer_pro: false, firm: true, corporate: true },
      },
    ],
  },
];

export const FAQ_ITEMS: FAQItem[] = [
  {
    q: 'Is there a free plan?',
    a: 'Yes. Every account gets 1 active case tracked for free. Paid plans are for more cases, bulk upload, and higher-volume workflows.',
  },
  {
    q: 'What counts against my limit?',
    a: 'Only active monitored cases count. Archived, removed, or disposed cases do not count unless monitoring is re-enabled.',
  },
  {
    q: 'What happens if I downgrade below current usage?',
    a: 'Existing cases are not deleted. You cannot add new active monitored cases until usage is within the new plan limit or you upgrade again.',
  },
  {
    q: 'How does the Firm plan work?',
    a: 'Firm is billed per seat with a 2-seat minimum. It includes 500 shared active monitored cases in one workspace. Public email domains can still be used by small chambers through manual admin invitations.',
  },
];

export const NEW_PRICING_FAQ_ITEMS: FAQItem[] = [
  {
    q: 'Is there a free plan?',
    a: 'Yes. Every account gets 1 case saved and checked for free, permanently. Individual litigants get a weekly automatic check, 3 manual refreshes per month, and one alert when the case is added. Live WhatsApp/email alerts and full order history are paid features.',
  },
  {
    q: 'What do the Case Pass options cover?',
    a: 'Both Case Passes upgrade one case with live status, full order history, and alerts on every update. Choose ₹599 for 6 months or ₹999 for 12 months from activation. The paid case does not use your free case allowance, and you can change the paid case once. There is no autopay or recurring charge.',
  },
  {
    q: 'What if I need to track more than one paid case?',
    a: 'Choose the annual multi-case plan and select 5, 10, 25, 50, or 100 active cases. It is a one-time payment for 12 months with no autopay. For larger caseloads, teams, or tailored workflows, request custom pricing.',
  },
  {
    q: 'What counts against my case limit?',
    a: 'Only active monitored cases count. Archived, removed, or disposed cases do not count unless monitoring is re-enabled.',
  },
  {
    q: 'What is your refund policy?',
    a: 'Refunds are reviewed case by case for genuine reasons. See indiacasestatus.com/refund-policy for details.',
  },
];
